// Business Profiles table: filters, cell text and sort keys, kept out of the component so they can be
// tested. Filtering here must match the backend's `filterProfiles`, because Export CSV sends the same
// filters to the server and has to download exactly the rows on screen.

export const DIRECTIONS = ["Collector", "Disburser", "Both", "Inactive"];
export const REACH_BANDS = ["low", "medium", "high"];
export const SIZE_BANDS = ["Small", "Medium", "Large"];

/** Filter keys, named as the profiles and export endpoints take them. Empty means "any". */
export function emptyFilters() {
  return {
    direction: "",
    country: "",
    collectionsReachBand: "",
    disbursementsReachBand: "",
    collectionsSizeBand: "",
    disbursementsSizeBand: "",
  };
}

const normaliseCountry = (country) => (country || "").trim().toLowerCase();

/** The companies matching every set filter (AND), as the backend filters them. */
export function filterProfiles(companies, filters) {
  const country = normaliseCountry(filters.country);
  return companies.filter(
    (c) =>
      (!filters.direction || c.direction === filters.direction) &&
      (!country || normaliseCountry(c.country) === country) &&
      (!filters.collectionsReachBand || c.reach.payers.band === filters.collectionsReachBand) &&
      (!filters.disbursementsReachBand || c.reach.payees.band === filters.disbursementsReachBand) &&
      (!filters.collectionsSizeBand || c.size.collections.band === filters.collectionsSizeBand) &&
      (!filters.disbursementsSizeBand || c.size.disbursements.band === filters.disbursementsSizeBand)
  );
}

/** The set filters only, as query params for the export. */
export function profileFilterParams(filters) {
  return Object.fromEntries(Object.entries(filters).filter(([, value]) => value));
}

/** Every country that appears, once each (ignoring case and spaces), sorted. */
export function countryOptions(companies) {
  const byKey = new Map();
  companies.forEach((c) => {
    const key = normaliseCountry(c.country);
    if (key && !byKey.has(key)) byKey.set(key, c.country.trim());
  });
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

/** "1–20", "21–100" or "over 100", from the limits in force. */
export function reachBandLabel(band, limits) {
  if (band === "low") return `1–${limits.lowMax}`;
  if (band === "medium") return `${limits.lowMax + 1}–${limits.mediumMax}`;
  if (band === "high") return `over ${limits.mediumMax}`;
  return "";
}

/** "GHS 120" in the company's main currency, never converted; empty with no transactions that way. */
export function averageText(size) {
  if (!size || size.currency == null || size.average == null) return "";
  return `${size.currency} ${Number(size.average).toLocaleString("en-GB", { maximumFractionDigits: 2 })}`;
}

/** The size band; "—" when there's an average but too few companies share its currency to band it. */
export function sizeBandText(size) {
  if (!size || size.average == null) return "";
  return size.band || "—";
}

/** Sort key for a size band: Small < Medium < Large, with no band below Small. */
export function sizeBandRank(band) {
  return SIZE_BANDS.indexOf(band) + 1;
}

/** One table row per company: display values plus flat sort keys. */
export function profileRow(company) {
  const { reach, size, walletHabits } = company;
  return {
    businessId: company.businessId,
    name: company.name,
    country: company.country || "",
    direction: company.direction,
    collectionsPerMonth: company.collections.perMonth,
    disbursementsPerMonth: company.disbursements.perMonth,
    payers: reach.payers.count,
    payees: reach.payees.count,
    collectionsAverage: averageText(size.collections),
    collectionsSize: sizeBandRank(size.collections.band),
    collectionsSizeText: sizeBandText(size.collections),
    disbursementsAverage: averageText(size.disbursements),
    disbursementsSize: sizeBandRank(size.disbursements.band),
    disbursementsSizeText: sizeBandText(size.disbursements),
    topUps: walletHabits.topUps.perMonth,
    withdrawals: walletHabits.withdrawals.perMonth,
    conversions: walletHabits.conversions.perMonth,
  };
}

/** Per-month figures to one decimal place, as the backend rounds them. */
export function formatPerMonth(value) {
  return Number(value || 0).toLocaleString("en-GB", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** `business-profiles-YYYY-MM-DD.csv` on today's London date, for when the server sends no name. */
export function exportFileName(now = new Date()) {
  const londonDate = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/London" }).format(now);
  return `business-profiles-${londonDate}.csv`;
}

/**
 * Where a company sits on the Collector → Disburser chart, or null when it can't be placed.
 * x: payees as a share of all its counterparties, 0 (only payers: a pure Collector) to 100 (only
 * payees: a pure Disburser). y: money moved per month in GBP, transactions per month × average
 * amount for each direction, converted at `rates` (GBP per unit). A direction whose currency has no
 * rate leaves the company off rather than understating it.
 */
export function profilePoint(company, rates) {
  const { payers, payees } = company.reach;
  const counterparties = payers.count + payees.count;
  if (!counterparties) return null;
  let perMonth = 0;
  for (const [flow, size] of [
    [company.collections, company.size.collections],
    [company.disbursements, company.size.disbursements],
  ]) {
    if (!flow.perMonth) continue;
    const rate = size.currency != null ? rates[size.currency] : undefined;
    if (rate == null || size.average == null) return null;
    perMonth += flow.perMonth * size.average * rate;
  }
  if (!(perMonth > 0)) return null;
  return {
    businessId: company.businessId,
    name: company.name,
    country: company.country || "",
    // Shown on the chart in place of a dot, so clusters of one country stand out.
    countryCode: company.countryCode || "?",
    direction: company.direction,
    payers: payers.count,
    payees: payees.count,
    x: (payees.count / counterparties) * 100,
    y: perMonth,
  };
}

/** The companies that can be placed on the chart, and how many were left off (no FX rate). */
export function chartPoints(companies, fx) {
  if (!fx) return { points: [], leftOff: 0 };
  const points = [];
  let leftOff = 0;
  companies.forEach((c) => {
    const point = profilePoint(c, fx.rates);
    if (point) points.push(point);
    else if (c.reach.payers.count + c.reach.payees.count > 0) leftOff += 1;
  });
  return { points, leftOff };
}
